import { useState, useRef, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import PriceConverter, { convertCurrency } from '../helpers/PriceConverter'
import InputNumber from '../forms/InputNumber'
import ContinueButtonStep2 from './ContinueButtonStep2'
import { findInObject, getTextFromInfo, roundPrice } from '../../utils'
import ConvertMetersToFeets from '../helpers/ConvertMetersToFeets'
import SelectSingleGuest from './SelectSingleGuest'
import useItinerary from './hooks/useItinerary'
import useCurrentShop from './hooks/useCurrentShop'
import { useSelector } from 'react-redux'
import { trackGA4Ecommerce } from '../../gtm'
import moment from 'moment'
import PropTypes from 'prop-types'

const AIR_CONDITIONING = 5
export default function CabinItem({
  cabin,
  selectedTrip,
  currency,
  isLastItem,
  callback,
  taxPercent,
  onSelectCallback,
  openRoomPopup,
  goToStep3,
}) {
  if(!cabin) {
    return null
  }
  const isLoaded = useRef()
  const { showItinerary } = useItinerary()
  const { shopInfo } = useCurrentShop()
  const { roomCategory: room } = cabin
  const [availability, setAvailability] = useState(cabin.availability)
  const [totalQty, setTotalQty] = useState(0)
  const hasSelectedCabins = useSelector(
    (state) => !!state.selectedCabins?.length,
  )

  function onCabinSelect(value, index) {
    if(isLoaded.current && cabin.pricesToShow[index].qty !== value) {
      const variant = cabin.pricesToShow[index]
      const data = {
        currency: 'USD',
        value: Number(
          roundPrice(convertCurrency(variant.priceNet, currency, 'USD')),
        ),
        items: [
          {
            item_name: shopInfo.title,
            item_id: cabin?.roomCategory?.id.toString(),
            price: Number(
              roundPrice(convertCurrency(variant.priceNet, currency, 'USD')),
            ),
            item_brand: 'PADI',
            item_category: 'PADI Travel',
            item_category2: 'Liveaboard',
            item_list_name: 'PADI Travel Liveaboards - Select Cabin',
            item_variant: `${selectedTrip.itinerary.title} / ${
              cabin?.roomCategory?.title
            } / ${moment(variant.startdateFromDate)
              .utc()
              .locale('en')
              .format('DD MMM')}-${moment(variant.dateTo)
              .utc()
              .locale('en')
              .format('DD MMM')} / ${
              variant.isDiver ? `${value} diver(s)` : `${value} non-diver(s)`
            }`,
            quantity: 1,
          },
        ],
      }
      trackGA4Ecommerce(
        variant.qty < value ? 'add_to_cart' : 'remove_from_cart',
        data,
        'PADI Travel - Liveaboards',
      )
      trackGA4Ecommerce(
        variant.qty < value
          ? 'checkout_la_cart_add_cabin'
          : 'checkout_la_cart_remove_cabin',
        data,
        'PADI Travel - Liveaboards',
      )
    }
    cabin.pricesToShow[index].qty = value

    let selectedCabinNumber = cabin.pricesToShow.reduce(function(acc, value) {
      return acc + (value.qty || 0)
    }, 0)

    setTotalQty(selectedCabinNumber)

    setAvailability(cabin.availability - selectedCabinNumber)
    if(onSelectCallback && typeof onSelectCallback === 'function') {
      onSelectCallback(cabin)
    }
  }

  useEffect(() => {
    isLoaded.current = true
  }, [])

  const openPopupHandle = (e, room) => {
    e.preventDefault()
    openRoomPopup(room)
  }

  return (
    <div className='checkout-cabin-wrapper'>
      <SelectSingleGuest
        isOpen={callback}
        roomTitle={room.title}
        onSelect={callback}
      />
      <div className='checkout-cabin'>
        <div
          className='cabin-info pointer'
          form-popup='form-popup'
          max-width='750'
          form-class='dr-room-popup'
          padding='20,30,20,30'
          ng-click='ctrl.cabin = cabin;'
          include-html='../templates/_cabin-info.html'
        >
          <p className='cabin-title'>{room.title}</p>
          <div className='photo-wrap'>
            <div className='cabin-photo'>
              <img
                src={room.images[0].thumbnails.PriceDesktop}
                title={room.images[0].thumbnails.pictureSource}
              />
            </div>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <div className='img-counter'>1/{room.images.length}</div>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <a
              className='room-more-info'
              onClick={(e) => openPopupHandle(e, room)}
            >
              &gt;&nbsp;
              <FormattedMessage
                id='show_full_cabin'
                defaultMessage='Show full cabin information'
              />
            </a>
          </div>
          <div className='cabin-details'>
            <ul>
              <li>
                <i className='font-icons peoples-icon' />
                <span className='property'>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <FormattedMessage
                    id='max_occupancy'
                    defaultMessage='Max occupancy'
                  />
                  :&nbsp;
                </span>
                <span className='bold'>{room.maxOccupancy}</span>
              </li>
              <li>
                <i className='font-icons room-icon' />
                <span className='property'>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <FormattedMessage id='beds' defaultMessage='Beds' />
                  :&nbsp;
                </span>
                <span className='bold'>
                  {getTextFromInfo(room.bedType, 'shop', 'BED_TYPE')}
                </span>
              </li>
              <li>
                <i className='font-icons length-icon' />
                <span className='property'>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <FormattedMessage id='room-size' defaultMessage='Room size' />
                  :&nbsp;
                </span>
                <span className='bold'>
                  <ConvertMetersToFeets
                    data={room.size}
                    currentUnits={shopInfo.areaFormat}
                  />
                </span>
              </li>
              <li>
                <i className='font-icons icon-shower' />
                <span className='property'>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <FormattedMessage
                    id='private_bathroom'
                    defaultMessage='Private bathroom'
                  />
                  :&nbsp;
                </span>
                <span className='bold'>
                  {findInObject(room.amenities, 1) ? (
                    <FormattedMessage id='yes' defaultMessage='Yes' />
                  ) : (
                    <FormattedMessage id='no' defaultMessage='No' />
                  )}
                </span>
              </li>
              <li>
                <i className='font-icons icon-condition' />
                <span className='property'>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <FormattedMessage
                    id='air_conditioning'
                    defaultMessage='Air Conditioning'
                  />
                  :&nbsp;
                </span>
                <span className='bold'>
                  {findInObject(room.amenities, AIR_CONDITIONING) ? (
                    <FormattedMessage id='yes' defaultMessage='Yes' />
                  ) : (
                    <FormattedMessage id='no' defaultMessage='No' />
                  )}
                </span>
              </li>
              <li>
                <i className='font-icons icon-network-new' />
                <span className='property'>
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <FormattedMessage id='shareable' defaultMessage='Shareable' />
                  :&nbsp;
                </span>
                <span className='bold'>
                  {cabin.isShared ? (
                    <FormattedMessage id='yes' defaultMessage='Yes' />
                  ) : (
                    <FormattedMessage id='no' defaultMessage='No' />
                  )}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className='cabin-prices'>
          <div className='cabin-prices__table'>
            <div className='cabin-prices__row header'>
              <div className='cabin-prices__combined-section'>
                <div className='cabin-prices__type'>
                  <FormattedMessage
                    id='package-type'
                    defaultMessage='Package type'
                  />
                </div>
                <div className='cabin-prices__price'>
                  <FormattedMessage
                    id='price-per-trip-1person'
                    defaultMessage='Price per trip for 1 person'
                  />
                </div>
              </div>
              <div className='cabin-prices__guests'>
                <FormattedMessage id='#guests' defaultMessage='# Guests' />
              </div>
            </div>
            {cabin.pricesToShow.map((variant, index) => {
              return (
                <div
                  key={variant.id}
                  className={`cabin-prices__row ${
                    !variant.isDiver ? 'gray-row' : ''
                  }
                  ${
                variant.compareAtPrice &&
                    variant.compareAtPrice > variant.priceNet
                  ? 'comparePrice'
                  : ''
                }`}
                >
                  <div className='cabin-prices__combined-section'>
                    <div className='cabin-prices__type'>
                      {variant.isDiver ? (
                        <span className='bold'>
                          <FormattedMessage id='diver' defaultMessage='Diver' />
                        </span>
                      ) : (
                        <span>
                          <FormattedMessage
                            id='non-diver'
                            defaultMessage='Non-diver'
                          />
                        </span>
                      )}
                    </div>
                    <div className='cabin-prices__price'>
                      <div className='price-wrapper'>
                        {variant.compareAtPrice &&
                          variant.compareAtPrice > variant.priceNet && (
                          <span className='old-price'>
                            <PriceConverter
                              amount={variant.compareAtPrice}
                              currency={currency}
                            />
                          </span>
                        )}
                        <span className='current-price'>
                          <PriceConverter
                            amount={variant.priceNet}
                            currency={currency}
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className='cabin-prices__guests'>
                    {cabin.itemAvailability === 0 && (
                      <span className='sold-out'>
                        <FormattedMessage
                          id='sold_out'
                          defaultMessage='Sold out'
                        />
                      </span>
                    )}
                    {cabin.itemAvailability > 0 && (
                      <div className='input-counter'>
                        <InputNumber
                          onChange={(value) => onCabinSelect(value, index)}
                          value={0}
                          plusDisabled={!availability}
                          preventMaxValueRewrite
                          minNumber={0}
                          plusMinusSeparate
                          maxNumber={availability}
                          disabled
                        />
                      </div>
                    )}
                    {cabin.itemAvailability <= 4 &&
                      cabin.itemAvailability > 0 &&
                      index + 1 === cabin.pricesToShow.length && (
                      <div className='available-spots'>
                        <FormattedMessage
                          id='only_spots_left'
                          values={{
                            spots: cabin.itemAvailability,
                          }}
                          defaultMessage='Only {spots, plural, one {# spot} other {# spots}} left'
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            <div className='price-includes-taxes'>
              {taxPercent ? (
                <FormattedMessage
                  id='excluding_taxes'
                  defaultMessage='Excluding {tax}% taxes'
                  values={{ tax: taxPercent }}
                />
              ) : (
                <FormattedMessage
                  id='including_taxes'
                  defaultMessage='Including taxes'
                />
              )}
            </div>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
            <a
              className='included-popup'
              onClick={() => showItinerary('#included-section')}
            >
              &gt;&nbsp;
              <FormattedMessage
                id='full_what-included'
                defaultMessage='Full details of what`s included and not'
              />
            </a>
          </div>
        </div>
      </div>
      {(isLastItem || !!totalQty) && (
        <ContinueButtonStep2
          selectedTrip={selectedTrip}
          onClick={goToStep3}
          disabled={!hasSelectedCabins}
        />
      )}
    </div>
  )
}

CabinItem.propTypes = {
  cabin: PropTypes.object,
  selectedTrip: PropTypes.object,
  goToStep3: PropTypes.func,
  isLastItem: PropTypes.bool,
  currency: PropTypes.string,
  callback: PropTypes.func,
  taxPercent: PropTypes.number,
  onSelectCallback: PropTypes.func,
  openRoomPopup: PropTypes.func,
}
